import React from 'react';
import { motion } from 'framer-motion';
import { Database, Tag, Layers, Eye } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { containerVariants, itemVariants } from '../../shared/animationVariants';
interface SubjectData {
  code: string;
  name: string;
  questionCount: number;
  familyCount: number;
  color: string;
  avgTier: number;
}
interface OverviewTabProps {
  subjects: SubjectData[];
  totalQuestions: number;
  totalFamilies: number;
  typeCount?: number;
  visualCount?: number;
  onBrowseSubject: (code: string) => void;
}
export function OverviewTab({
  subjects,
  totalQuestions,
  totalFamilies,
  typeCount = 0,
  visualCount = 0,
  onBrowseSubject
}: OverviewTabProps) {
  const activeSubjects = subjects.filter(s => s.questionCount > 0).length;
  return <div className="space-y-6" data-id="element-6001" data-mp="iepg3391f33e">
      {/* Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-6002" data-mp="3hmwkm4w39ety">
        <motion.div variants={itemVariants} data-id="element-6003" data-mp="38kndzq3xy5lu">
          <StatCard label="Totaal Vragen" value={totalQuestions.toLocaleString('nl-NL')} icon={<Database className="w-5 h-5" data-id="element-6005" data-mp="54qag00abvwh" />} color="blue" data-id="element-6004" data-mp="38njada054m86" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6006" data-mp="5n328wu1dlc">
          <StatCard label="Families" value={totalFamilies} icon={<Tag className="w-5 h-5" data-id="element-6008" data-mp="3rj9f34zgjwpr" />} color="purple" data-id="element-6007" data-mp="6y96fdr2xfm" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6009" data-mp="3mk6kkeh3t8w1">
          <StatCard label="Vraagtypen" value={typeCount} icon={<Layers className="w-5 h-5" data-id="element-6011" data-mp="nwiwfagxsgup" />} color="emerald" data-id="element-6010" data-mp="3mjdl7ykh5lnx" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6012" data-mp="j2ek0bowjgm8">
          <StatCard label="Actieve Vakken" value={`${activeSubjects} / ${subjects.length}`} icon={<Eye className="w-5 h-5" data-id="element-6014" data-mp="2hcn6tzlcaacp" />} color="amber" data-id="element-6013" data-mp="2dbtl9cvdd7mz" />
        </motion.div>
      </motion.div>

      {/* Subject Cards Grid */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-6015" data-mp="28lmwnd0uxn4q">
        {subjects.map(subject => {
        const isActive = subject.questionCount > 0;
        return <motion.div key={subject.code} variants={itemVariants} data-id="element-6016" data-mp="1knxo671w1dpo">
              <Card className={`p-5 h-full flex flex-col transition-all duration-200 cursor-pointer border border-gray-200 ${isActive ? 'hover:shadow-md hover:border-blue-300' : 'opacity-50 bg-gray-50/50 cursor-not-allowed'}`} onClick={() => isActive && onBrowseSubject(subject.code)} data-id="element-6017" data-mp="1kpqz3yjjak3h">
                {/* Header */}
                <div className="flex items-start justify-between mb-4" data-id="element-6018" data-mp="3u5i6d1b91fzf">
                  <div className="flex items-center gap-3" data-id="element-6019" data-mp="3p65j4fggu6wb">
                    <div className={`w-11 h-11 rounded-xl ${subject.color} flex items-center justify-center shadow-sm`} data-id="element-6020" data-mp="3p3j5by2zev5n">
                      <span className="text-white font-black text-sm" data-id="element-6021" data-mp="204k5kw4ncek">
                        {subject.code.slice(0, 2)}
                      </span>
                    </div>
                    <div className="min-w-0" data-id="element-6022" data-mp="bripwaeo98g5">
                      <h3 className="font-bold text-gray-900 text-sm truncate" data-id="element-6023" data-mp="bstsi6wdsa5o">
                        {subject.name}
                      </h3>
                      <code className="text-[10px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-6024" data-mp="lodp6virrcvh">
                        {subject.code}
                      </code>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-2.5 mb-4 flex-1" data-id="element-6025" data-mp="2oxfm01vabfhd">
                  <div className="flex items-center justify-between" data-id="element-6026" data-mp="2owxf8bazxiv7">
                    <span className="text-xs text-gray-500" data-id="element-6027" data-mp="2tvqti5y3xdwf">Vragen</span>
                    <span className={`text-sm font-bold tabular-nums ${isActive ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-6028" data-mp="2xydgntju2ana">
                      {subject.questionCount.toLocaleString('nl-NL')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-6029" data-mp="2654o6mfhm7q4">
                    <span className="text-xs text-gray-500" data-id="element-6030" data-mp="2a92qf603zyne">Families</span>
                    <span className="text-sm font-medium text-gray-700 tabular-nums" data-id="element-6031" data-mp="2f6ky6t2e47tt">
                      {subject.familyCount}
                    </span>
                  </div>
                  {isActive && subject.avgTier > 0 && <div className="flex items-center justify-between" data-id="element-6032" data-mp="1mgvv8a64dxpk">
                      <span className="text-xs text-gray-500" data-id="element-6033" data-mp="1rgqkk9a8yf8i">Gem. Tier</span>
                      <Badge variant="neutral" size="sm" data-id="element-6034" data-mp="1jysigpuea6fb">
                        {subject.avgTier}
                      </Badge>
                    </div>}
                </div>

                {/* Distribution bar */}
                {isActive && <div className="mb-4" data-id="element-6035" data-mp="15yrdozb0eeuk">
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-6036" data-mp="15xzjnsx28p6e">
                      <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${Math.min(100, subject.questionCount / Math.max(totalQuestions, 1) * 100)}%`
                }} transition={{
                  duration: 0.6,
                  delay: 0.1
                }} className={`h-full rounded-full ${subject.color}`} data-id="element-6037" data-mp="10zy4vk6o3c0q" />
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1 text-right" data-id="element-6038" data-mp="3mpzji2nv1eh">
                      {Math.round(subject.questionCount / Math.max(totalQuestions, 1) * 100)}
                      % van totaal
                    </div>
                  </div>}

                {/* Action */}
                <div className="mt-auto pt-3 border-t border-gray-100" data-id="element-6039" data-mp="mczd1o3weflh">
                  <Button variant="secondary" size="sm" className="w-full justify-center" onClick={() => isActive && onBrowseSubject(subject.code)} disabled={!isActive} data-id="element-6040" data-mp="mdr7gvlre7a6">
                    <Eye className="w-4 h-4 mr-1.5" data-id="element-6041" data-mp="hkodwngzextz" />
                    Bekijk {subject.questionCount} vragen
                  </Button>
                </div>
              </Card>
            </motion.div>;
      })}
      </motion.div>
    </div>;
}